import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProjectCard from './components/ProjectCard';
import FilterBar from './components/FilterBar';
import StatsOverview from './components/StatsOverview';
import EmptyState from './components/EmptyState';
import ProjectTimeline from './components/ProjectTimeline';

const Portfolio = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedTechnology, setSelectedTechnology] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedComplexity, setSelectedComplexity] = useState('');
  const [showTimeline, setShowTimeline] = useState(false);

  const projects = [
    {
      id: 1,
      title: "Sode Professional Community",
      description: "A modern social platform for professionals to connect, collaborate, and share industry insights. Sode enables networking, knowledge exchange, and community-driven growth with robust privacy and engagement features.",
      image: "/assets/images/sode-logo.jpg",
      imageAlt: "Sode Professional Community Logo - vibrant purple logo representing professional networking and collaboration",
      industry: "Community",
      type: "Web Application",
      complexity: "Enterprise",
      technologies: ["React", "Node.js", "PostgreSQL", "Redis", "AWS", "WebSockets"],
      metrics: [
        { value: "100K+", label: "Active Members" },
        { value: "99.9%", label: "Uptime" },
        { value: "1s", label: "Avg Response Time" }
      ],
      year: "2024",
      featured: true,
      isNew: true,
      liveUrl: "https://sode.it.com"
    },
    {
      id: 2,
      title: "Annunciation Seminary Amaudara",
      description: "A professional and modern school website for Annunciation Seminary Amaudara. The platform provides information about the school, academic programs, admissions, news, events, and a portal for students and staff. Designed for accessibility, engagement, and seamless communication within the school community.",
      image: "/assets/images/asa-pic.jpg",
      imageAlt: "Annunciation Seminary Amaudara - professional school website homepage with academic and community features",
      industry: "Education",
      type: "Web Application",
      complexity: "Medium",
      technologies: ["React", "Node.js", "MongoDB", "Express", "Bootstrap"],
      metrics: [
        { value: "1K+", label: "Students" },
        { value: "24/7", label: "Online Access" },
        { value: "100%", label: "Mobile Friendly" }
      ],
      year: "2024",
      featured: true,
      liveUrl: "https://annunciationseminaryamaudara.online"
    },
    {
      id: 3,
      title: "E-Commerce Marketplace",
      description: "Multi-vendor marketplace platform with AI-powered recommendations, processing 10,000+ orders monthly with automated inventory management",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1eb7c1cb3-1764655134961.png",
      imageAlt: "E-commerce shopping interface showing product grid with colorful items, shopping cart icon, and promotional banners on laptop screen",
      industry: "E-Commerce",
      type: "Web Application",
      complexity: "Complex",
      technologies: ["React", "Django", "PostgreSQL", "Elasticsearch", "Stripe", "AWS S3"],
      metrics: [
        { value: "10K+", label: "Monthly Orders" },
        { value: "35%", label: "Conversion Rate" },
        { value: "2.5s", label: "Page Load" }],
      year: "2024",
      liveUrl: "https://example.com"
    },
    {
      id: 4,
      title: "EdTech Learning Platform",
      description: "Interactive online learning platform with video streaming, live classes, and progress tracking serving 25,000+ students globally",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e32c9d5e-1766805495998.png",
      imageAlt: "Student using laptop for online learning with video conference interface showing instructor and course materials in bright study room",
      industry: "Education",
      type: "Web Application",
      complexity: "Complex",
      technologies: ["Vue.js", "Node.js", "MySQL", "WebRTC", "AWS CloudFront", "Zoom API"],
      metrics: [
        { value: "25K+", label: "Students" },
        { value: "92%", label: "Completion Rate" },
        { value: "4.8/5", label: "Rating" }],
      year: "2023",
      featured: true,
      liveUrl: "https://example.com"
    },
    {
      id: 5,
      title: "Goshen Prinath Academy",
      description: "Comprehensive primary/seconadry school management system with student enrollment, parent communication, and academic performance tracking for Nigerian schools",
      image: "/assets/images/goshen-pic.jpg",
      imageAlt: "Modern luxury house exterior with large windows and landscaped garden displayed on real estate listing website interface",
      industry: "A primary/seconadry school in nigeria",
      type: "Web Application",
      complexity: "Medium",
      technologies: ["React", "Express", "MongoDB", "Mapbox", "Twilio", "SendGrid"],
      metrics: [
        { value: "500+", label: "Active Agents" },
        { value: "60%", label: "Lead Conversion" },
        { value: "3x", label: "Productivity" }],
      year: "2023",
      liveUrl: "https://goshenacademy.site"
    },
    {
      id: 6,
      title: "SaaS Analytics Dashboard",
      description: "Real-time business intelligence platform with custom reporting, data visualization, and API integrations for enterprise clients",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ecd42800-1767033343981.png",
      imageAlt: "Business analytics dashboard showing multiple colorful charts, graphs, and KPI metrics on large monitor in modern office environment",
      industry: "SaaS",
      type: "Web Application",
      complexity: "Complex",
      technologies: ["React", "Python", "PostgreSQL", "D3.js", "Redis", "Docker"],
      metrics: [
        { value: "1M+", label: "Data Points" },
        { value: "100ms", label: "Query Time" },
        { value: "99.9%", label: "Accuracy" }],
      year: "2023",
      liveUrl: "https://example.com"
    },
    {
      id: 7,
      title: "Restaurant Management System",
      description: "All-in-one solution for restaurant operations including POS, inventory, staff scheduling, and customer loyalty program",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_14b2bf197-1766490814463.png",
      imageAlt: "Restaurant interior with elegant table settings and modern POS system tablet showing order management interface on counter",
      industry: "Hospitality",
      type: "Web Application",
      complexity: "Medium",
      technologies: ["Angular", "Node.js", "MySQL", "Socket.io", "Square API", "Twilio"],
      metrics: [
        { value: "200+", label: "Restaurants" },
        { value: "45%", label: "Efficiency Gain" },
        { value: "30%", label: "Cost Savings" }],
      year: "2023",
      liveUrl: "https://example.com"
    },
    {
      id: 8,
      title: "Fitness Tracking App",
      description: "Mobile-first fitness platform with workout plans, nutrition tracking, and social features connecting 15,000+ fitness enthusiasts",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f07f0639-1766818953212.png",
      imageAlt: "Athletic person exercising in modern gym with smartphone displaying fitness tracking app showing workout progress and heart rate monitor",
      industry: "Health & Fitness",
      type: "Progressive Web App",
      complexity: "Medium",
      technologies: ["React", "Firebase", "PWA", "Chart.js", "Stripe", "Push Notifications"],
      metrics: [
        { value: "15K+", label: "Active Users" },
        { value: "85%", label: "Retention" },
        { value: "4.7/5", label: "App Rating" }],
      year: "2022",
      liveUrl: "https://example.com"
    },
    {
      id: 9,
      title: "Legal Document Automation",
      description: "AI-powered legal document generation and management system reducing document preparation time by 70% for law firms",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fbd42359-1764664074476.png",
      imageAlt: "Professional lawyer reviewing legal documents on laptop with digital signature interface and case management system in modern law office",
      industry: "Legal",
      type: "Web Application",
      complexity: "Complex",
      technologies: ["Vue.js", "Python", "PostgreSQL", "NLP", "DocuSign API", "AWS Lambda"],
      metrics: [
        { value: "70%", label: "Time Saved" },
        { value: "10K+", label: "Documents" },
        { value: "99.5%", label: "Accuracy" }],
      year: "2022",
      liveUrl: "https://example.com"
    },
    {
      id: 10,
      title: "Event Management Platform",
      description: "Comprehensive event planning and ticketing system with virtual event support, handling 500+ events annually",
      image: "https://images.unsplash.com/photo-1726179205333-2007ae6418d2",
      imageAlt: "Large conference hall with audience attending event showing stage presentation and mobile app interface for event check-in and networking",
      industry: "Events",
      type: "Web Application",
      complexity: "Medium",
      technologies: ["React", "Node.js", "MongoDB", "Stripe", "SendGrid", "QR Code"],
      metrics: [
        { value: "500+", label: "Events/Year" },
        { value: "50K+", label: "Attendees" },
        { value: "98%", label: "Check-in Rate" }],
      year: "2022",
      liveUrl: "https://example.com"
    },
    {
      id: 11,
      title: "Supply Chain Tracker",
      description: "Real-time logistics and inventory management system with IoT integration tracking 100,000+ shipments monthly",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_135ea089a-1764637482598.png",
      imageAlt: "Warehouse interior with organized inventory shelves and worker using tablet showing supply chain tracking dashboard with shipment locations",
      industry: "Logistics",
      type: "Web Application",
      complexity: "Enterprise",
      technologies: ["React", "Java", "Oracle", "IoT", "Google Maps API", "Kafka"],
      metrics: [
        { value: "100K+", label: "Shipments" },
        { value: "99.8%", label: "On-Time" },
        { value: "25%", label: "Cost Reduction" }],
      year: "2022",
      liveUrl: "https://example.com"
    },
    {
      id: 12,
      title: "Social Media Dashboard",
      description: "Multi-platform social media management tool with scheduling, analytics, and team collaboration features for agencies",
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_179a29bb3-1764669852473.png",
      imageAlt: "Marketing professional analyzing social media metrics on multiple screens showing engagement graphs, post scheduler, and content calendar",
      industry: "Marketing",
      type: "Web Application",
      complexity: "Complex",
      technologies: ["React", "Node.js", "PostgreSQL", "Redis", "Social APIs", "Bull Queue"],
      metrics: [
        { value: "5K+", label: "Agencies" },
        { value: "80%", label: "Time Saved" },
        { value: "3x", label: "Engagement" }],
      year: "2021",
      liveUrl: "https://nexus-gamma-sable.vercel.app"
    }];


  const stats = [
  { icon: "Briefcase", value: "50+", label: "Projects Completed" },
  { icon: "Users", value: "200K+", label: "Users Served" },
  { icon: "TrendingUp", value: "99.8%", label: "Client Satisfaction" },
  { icon: "Award", value: "15+", label: "Industry Awards" }];


  const industryOptions = [
  { value: "FinTech", label: "FinTech" },
  { value: "Healthcare", label: "Healthcare" },
  { value: "E-Commerce", label: "E-Commerce" },
  { value: "Education", label: "Education" },
  { value: "Real Estate", label: "Real Estate" },
  { value: "SaaS", label: "SaaS" },
  { value: "Hospitality", label: "Hospitality" },
  { value: "Health & Fitness", label: "Health & Fitness" },
  { value: "Legal", label: "Legal" },
  { value: "Events", label: "Events" },
  { value: "Logistics", label: "Logistics" },
  { value: "Marketing", label: "Marketing" }];


  const technologyOptions = [
  { value: "React", label: "React" },
  { value: "Node.js", label: "Node.js" },
  { value: "Next.js", label: "Next.js" },
  { value: "Vue.js", label: "Vue.js" },
  { value: "Angular", label: "Angular" },
  { value: "TypeScript", label: "TypeScript" },
  { value: "Python", label: "Python" },
  { value: "Django", label: "Django" },
  { value: "PostgreSQL", label: "PostgreSQL" },
  { value: "MongoDB", label: "MongoDB" },
  { value: "AWS", label: "AWS" },
  { value: "Docker", label: "Docker" }];


  const typeOptions = [
  { value: "Web Application", label: "Web Application" },
  { value: "Progressive Web App", label: "Progressive Web App" },
  { value: "Mobile App", label: "Mobile App" },
  { value: "API Development", label: "API Development" }];


  const complexityOptions = [
  { value: "Simple", label: "Simple" },
  { value: "Medium", label: "Medium" },
  { value: "Complex", label: "Complex" },
  { value: "Enterprise", label: "Enterprise" }];


  const filteredProjects = useMemo(() => {
    return projects?.filter((project) => {
      const matchesSearch = searchQuery === '' ||
      project?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      project?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      project?.technologies?.some((tech) => tech?.toLowerCase()?.includes(searchQuery?.toLowerCase()));

      const matchesIndustry = selectedIndustry === '' || project?.industry === selectedIndustry;
      const matchesTechnology = selectedTechnology === '' || project?.technologies?.includes(selectedTechnology);
      const matchesType = selectedType === '' || project?.type === selectedType;
      const matchesComplexity = selectedComplexity === '' || project?.complexity === selectedComplexity;

      return matchesSearch && matchesIndustry && matchesTechnology && matchesType && matchesComplexity;
    });
  }, [projects, searchQuery, selectedIndustry, selectedTechnology, selectedType, selectedComplexity]);

  const activeFiltersCount = [selectedIndustry, selectedTechnology, selectedType, selectedComplexity, searchQuery]?.filter(Boolean)?.length;

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedIndustry('');
    setSelectedTechnology('');
    setSelectedType('');
    setSelectedComplexity('');
  };

  return (
    <>
      <Helmet>
        <title>Portfolio - Production-Ready Projects | MikeDev Pro</title>
        <meta name="description" content="Explore 50+ production-grade projects delivering measurable business impact. Case studies showcasing full-stack development expertise across FinTech, Healthcare, E-Commerce, and more." />
      </Helmet>
      <Header />
      <main className="min-h-screen bg-background pt-16">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 md:py-12 lg:py-16">
          <Breadcrumbs />

          <div className="mb-8 md:mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-lg bg-primary/10">
                <Icon name="Briefcase" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-foreground">
                  Production Portfolio
                </h1>
                <p className="text-sm md:text-base text-muted-foreground mt-1">
                  Case studies demonstrating real-world impact and technical excellence
                </p>
              </div>
            </div>
            <p className="text-base md:text-lg text-muted-foreground max-w-3xl">
              Explore production-grade projects that solve real business challenges. Each case study showcases measurable outcomes, technical architecture decisions, and the problem-solving approach that drives results.
            </p>
          </div>

          <StatsOverview stats={stats} />

          <FilterBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedIndustry={selectedIndustry}
            onIndustryChange={setSelectedIndustry}
            selectedTechnology={selectedTechnology}
            onTechnologyChange={setSelectedTechnology}
            selectedType={selectedType}
            onTypeChange={setSelectedType}
            selectedComplexity={selectedComplexity}
            onComplexityChange={setSelectedComplexity}
            onClearFilters={handleClearFilters}
            industryOptions={industryOptions}
            technologyOptions={technologyOptions}
            typeOptions={typeOptions}
            complexityOptions={complexityOptions}
            activeFiltersCount={activeFiltersCount} />


          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground">
                {filteredProjects?.length} {filteredProjects?.length === 1 ? 'Project' : 'Projects'}
              </h2>
              {activeFiltersCount > 0 &&
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                  {activeFiltersCount} {activeFiltersCount === 1 ? 'Filter' : 'Filters'} Active
                </span>
              }
            </div>
            <Button
              variant={showTimeline ? "default" : "outline"}
              onClick={() => setShowTimeline(!showTimeline)}
              iconName={showTimeline ? "Grid3x3" : "Clock"}
              iconPosition="left"
              className="hidden md:flex">

              {showTimeline ? 'Grid View' : 'Timeline View'}
            </Button>
          </div>

          {filteredProjects?.length === 0 ?
          <EmptyState onClearFilters={handleClearFilters} /> :
          showTimeline ?
          <ProjectTimeline projects={filteredProjects} /> :

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-12">
              {filteredProjects?.map((project) =>
            <ProjectCard key={project?.id} project={project} />
            )}
            </div>
          }

          <div className="bg-card rounded-xl border border-border p-6 md:p-8 lg:p-10 text-center">
            <div className="flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/10 mx-auto mb-4 md:mb-6">
              <Icon name="Rocket" size={32} color="var(--color-primary)" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground mb-3 md:mb-4">
              Ready to Start Your Project?
            </h2>
            <p className="text-base md:text-lg text-muted-foreground mb-6 md:mb-8 max-w-2xl mx-auto">
              Let's discuss how I can help you build scalable, production-ready solutions that drive real business results. Available for new projects starting Q1 2025.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                variant="default"
                iconName="Mail"
                iconPosition="left"
                onClick={() => window.location.href = '/contact'}>

                Start a Project
              </Button>
              <Button
                variant="outline"
                iconName="Calendar"
                iconPosition="left"
                onClick={() => window.open('https://calendly.com', '_blank')}>

                Schedule Consultation
              </Button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>);

};

export default Portfolio;